// ----------------------------------------------------------------------
// <copyright file="Izenda.DatabaseMappingDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var m = require('mobx');
  var trackable = require('share/tracking/Izenda.Trackable');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var constants = require('logic/data/Izenda.DatabaseMappingConstants');
  var DatabaseMapping = require('logic/models/Izenda.DatabaseMapping');

  const {
    filter,
    some,
    forEach,
    map,
    isNil,
    isEqual,
    flow,
    uniqBy,
    flatten,
    concat,
    uniq,
    find
  } = require('lodash/fp');

  function DatabaseMappingDetail(data) {
    var props = {
      mappings: [], // Collection of DatabaseMapping
      doesMergeDuplicate: false,
      selectedTenantId: null,
      selectedMappings: function() {
        var result;
        if (isEqual(constants.ALL_TENANT_ID, this.selectedTenantId)) {
          result = this.mappings;
        } else {
          result = filter(
            function(item) {
              return item.hasTenant(this.selectedTenantId);
            }.bind(this),
            this.mappings
          );
        }

        return filter(function(item) {
          return !item.checkState(ObjectStateConstants.DELETE);
        }, result);
      },
      doesShowMergeDuplicateMapping: function() {
        return constants.ALL_TENANT_ID === this.selectedTenantId;
      }
    };

    m.extendObservable(trackable(this), props);
    data && this.populate(data);

    this.disposeObserveMergeDuplicate = m.observe(
      this,
      'doesMergeDuplicate',
      function() {
        this.mergeMappings();
      }.bind(this)
    );

    this.resetDirty();
  }

  /**
   * Clean.
   */
  DatabaseMappingDetail.prototype.destroy = function() {
    this.disposeObserveMergeDuplicate();
  };

  /**
   * Populate data.
   * @param {Object} data
   */
  DatabaseMappingDetail.prototype.populate = m.action(function populate(data) {
    this.mappings.clear();
    forEach(
      function(item) {
        this.mappings.push(new DatabaseMapping(item));
      }.bind(this),
      data
    );
  });

  /**
   * Delete a mapping.
   * @param {String} id
   */
  DatabaseMappingDetail.prototype.deleteMapping = m.action(function deleteMapping(id) {
    var mapping = find(
      {
        id: id
      },
      this.mappings
    );

    if (isNil(mapping)) {
      return;
    }

    if (mapping.isNew()) {
      this.mappings.remove(mapping);
      this.markDirty();
    } else {
      mapping.setPropertyValue('state', ObjectStateConstants.DELETE);
    }
  });

  /**
   * Add a new mapping.
   */
  DatabaseMappingDetail.prototype.addMapping = m.action(function addMapping(fromServer, fromDatabaseName, fromDatabaseUser) {
    this.mappings.unshift(
      new DatabaseMapping({
        tenants: [constants.ALL_TENANT_ID],
        state: ObjectStateConstants.INSERT,
        fromServer: fromServer,
        fromDatabaseName: fromDatabaseName,
        fromDatabaseUser: fromDatabaseUser
      })
    );
    this.markDirty();
  });

  /**
   * Update value of property.
   * @param {String} key
   * @param {Object} value
   */
  DatabaseMappingDetail.prototype.setPropertyValue = m.action(function updatePropertyValue(key, value, donotSetDirty) {
    this[key] = value;
    !donotSetDirty && this.markDirty();
  });

  /**
   * Check dirty status.
   */
  DatabaseMappingDetail.prototype.checkDirty = function() {
    if (this.isDirty) {
      return this.isDirty;
    }

    var result = false;
    forEach(function(item) {
      if (item.isDirty) {
        result = item.isDirty;
        return false;
      }
    }, this.mappings);

    return result;
  };

  /**
   * Calculate data for saving.
   */
  DatabaseMappingDetail.prototype.buildDataForSaving = function() {
    return flow(
      map(function(item) {
        return item.buildDataForSaving();
      }),
      filter(function(item) {
        return item.state !== ObjectStateConstants.NONE;
      })
    )(this.mappings);
  };

  /**
   * Merge mappings
   */
  DatabaseMappingDetail.prototype.mergeMappings = m.action(function mergeMappings() {
    if (!this.doesMergeDuplicate || this.mappings.length <= 1) return;

    var pureMappings = this.mappings.peek();
    var distinctMappings = uniqBy('hashValue', this.mappings.peek());

    var prepareMappings = m.action(function() {
      forEach(function(item) {
        var matchingItems = flow(
          filter(function(mapping) {
            return item.hashValue === mapping.hashValue;
          }),
          map(function(item) {
            return item.tenants.peek();
          }),
          flatten,
          concat(item.tenants.peek()),
          uniq
        )(pureMappings);

        var doesHasAllTenant = some(function(item) {
          return constants.ALL_TENANT_ID === item;
        }, matchingItems);
        doesHasAllTenant && (matchingItems = [constants.ALL_TENANT_ID]);

        item.tenants = matchingItems;
      }, distinctMappings);
    });

    var merge = m.action(
      function() {
        var doesNeedToSetDirty = false;
        var i = this.mappings.length - 1;
        for (; i >= 0; i--) {
          var matchItem = find(
            function(item) {
              return this.mappings[i].id === item.id;
            }.bind(this),
            distinctMappings
          );

          if (matchItem) {
            this.mappings[i].tenants = matchItem.tenants;
            doesNeedToSetDirty = true;
          } else {
            this.deleteMapping(this.mappings[i].id);
          }
        }

        doesNeedToSetDirty && this.markDirty();
      }.bind(this)
    );

    prepareMappings();
    merge();
  });

  /**
   * Update mapping error details
   * @param {Number} index
   * @param {String} message
   */
  DatabaseMappingDetail.prototype.setMappingError = function(index, message) {
    this.mappings[index].setPropertyValue('message', message);
  };

  /**
   * Reset mapping errors.
   */
  DatabaseMappingDetail.prototype.resetMessage = m.action(function() {
    forEach(function(item) {
      item.setPropertyValue('message', null);
    }, this.mappings);
  });

  module.exports = DatabaseMappingDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.DatabaseMappingDetail.js